import { logKey } from '@/platform/shortcuts/operator-shortcuts';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { SalesPortalShortcuts } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SEARCH_TRIGGER_KEY } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-constants';
import { useCallback } from 'react';

export interface OrderEntryInputCellShortcutCallbacks {
  onOpenRestaurantSelector?: () => void;
  onSwitchBuildReviewMode?: () => void;
  onToggleCheckout?: () => void;
  onTriggerSearch?: () => void;
}

/**
 * Handles order-entry shortcut keys (/, r, l, c) for editable table cells.
 *
 * Returns a pre-handler that can be passed to `useCellKeyboardNavigation`'s
 * `onPreKeyPress` option. When a shortcut key is detected it invokes the
 * matching callback, calls `clearFocusAndBlur`, logs the key, and returns
 * `true` so the navigation hook skips further processing.
 */
export const useOrderEntryCellShortcuts = (
  callbacks?: OrderEntryInputCellShortcutCallbacks,
): ((
  e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  clearFocusAndBlur: () => void,
) => boolean) => {
  return useCallback(
    (
      e: NativeSyntheticEvent<TextInputKeyPressEventData>,
      clearFocusAndBlur: () => void,
    ): boolean => {
      if (!callbacks) {
        return false;
      }

      const key = e.nativeEvent.key;

      if (key === SEARCH_TRIGGER_KEY) {
        e.preventDefault?.();
        clearFocusAndBlur();
        logKey(SalesPortalShortcuts.Search, {});
        callbacks.onTriggerSearch?.();
        return true;
      }

      if (key === SalesPortalShortcuts.SwitchBuildReviewMode) {
        e.preventDefault?.();
        clearFocusAndBlur();
        logKey(SalesPortalShortcuts.SwitchBuildReviewMode, {});
        callbacks.onSwitchBuildReviewMode?.();
        return true;
      }

      if (key === SalesPortalShortcuts.OpenRestaurantSelector) {
        e.preventDefault?.();
        clearFocusAndBlur();
        logKey(SalesPortalShortcuts.OpenRestaurantSelector, {});
        callbacks.onOpenRestaurantSelector?.();
        return true;
      }

      if (key === SalesPortalShortcuts.ToggleCheckout) {
        e.preventDefault?.();
        clearFocusAndBlur();
        logKey(SalesPortalShortcuts.ToggleCheckout, {});
        callbacks.onToggleCheckout?.();
        return true;
      }

      return false;
    },
    [callbacks],
  );
};
